import { useState, useEffect } from 'react'
import { Link, useNavigate  } from 'react-router-dom'
import {
    Users, CalendarCheck, Calendar, TrendingUp, AlertCircle,
    Clock, CheckCircle, Check, Plus, MessageSquare, Download,
    X, FileSpreadsheet, Send, Mail, ChevronRight, PlayCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function DashboardPage() {
    const [stats, setStats] = useState(null)
    const navigate = useNavigate()
    const [upcomingAppointments, setUpcomingAppointments] = useState([])
    const [sysConfig, setSysConfig] = useState({})

    const [tasks, setTasks] = useState([])
    const [pendingFormAlerts, setPendingFormAlerts] = useState([])

    // États ajout rapide de tâche
    const [newTaskContent, setNewTaskContent] = useState('')
    const [newTaskDate, setNewTaskDate]       = useState('')

    const [loading, setLoading] = useState(true)

    // Export CSV
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [exportDates, setExportDates] = useState({ start: '', end: '' })

    // Modale relance formulaire depuis le dashboard
    const [relanceModal, setRelanceModal]     = useState(null) // { req_id, template_name, patient_name, patient_email, patient_phone }
    const [sendVia, setSendVia]               = useState({ email: true, sms: false })
    const [isSending, setIsSending]           = useState(false)

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        try {
            const[statsRes, aptsRes, tasksRes, alertsRes, settingsRes] = await Promise.all([
                api.get('/api/admin/dashboard'),
                // OPTIMISATION : On demande strictement 5 résultats au serveur (Allège le réseau et la RAM) !
                api.get('/api/appointments/', { params: { from_date: new Date().toISOString().split('T')[0], status: 'confirmed', limit: 5 } }),
                api.get('/api/tasks/?pending_only=true'),
                api.get('/api/forms/requests/pending-alerts'),
                api.get('/api/settings/') // 
            ])
            const conf = {}; settingsRes.data.forEach(s => conf[s.setting_key] = s.setting_value);
            setSysConfig(conf);

            setStats(statsRes.data)
            setUpcomingAppointments(aptsRes.data)
            setTasks(tasksRes.data)
            setPendingFormAlerts(alertsRes.data)
        } catch (error) {
            console.error(error)
            toast.error("Erreur lors du chargement des données")
        } finally {
            setLoading(false)
        }
    }

    const handleAddTask = async (e) => {
        e.preventDefault()
        if (!newTaskContent.trim()) return
        try {
            const res = await api.post('/api/tasks/', {
                content: newTaskContent,
                due_date: newTaskDate || null
            })
            setTasks([res.data, ...tasks])
            setNewTaskContent('')
            setNewTaskDate('')
            toast.success("Tâche ajoutée")
        } catch (err) {
            toast.error("Erreur d'ajout")
        }
    }

    const toggleTask = async (task) => {
        try {
            setTasks(tasks.filter(t => t.id !== task.id))
            await api.patch(`/api/tasks/${task.id}`, { is_completed: true })
            toast.success("Tâche terminée !")
        } catch (err) {
            toast.error("Erreur")
            fetchData()
        }
    }

    // Relance depuis le dashboard
    const handleRelance = async () => {
        if (!sendVia.email && !sendVia.sms) {
            return toast.error("Sélectionnez au moins un canal.")
        }
        setIsSending(true)
        const toastId = toast.loading("Envoi en cours...")
        let errors = []
        try {
            if (sendVia.email) {
                try { await api.post(`/api/forms/requests/${relanceModal.req_id}/send-email`) }
                catch { errors.push("Email") }
            }
            if (sendVia.sms) {
                try { await api.post(`/api/forms/requests/${relanceModal.req_id}/send-sms`) }
                catch { errors.push("SMS") }
            }
            if (errors.length === 0) {
                toast.success(
                    sendVia.email && sendVia.sms ? "Relance envoyée (Email + SMS) !" :
                    sendVia.email ? "Relance par Email envoyée !" : "Relance par SMS envoyée !",
                    { id: toastId }
                )
                setRelanceModal(null)
                // On retire l'alerte de la liste localement (pas besoin de recharger tout)
                setPendingFormAlerts(prev => prev.filter(a => a.req_id !== relanceModal.req_id))
            } else {
                toast.error(`Échec : ${errors.join(" et ")}`, { id: toastId })
            }
        } finally {
            setIsSending(false)
        }
    }

    const getConsultationTypeLabel = (type) => {
        const labels = { cabinet: '🏥 Cabinet', video: '📹 Vidéo', domicile: '🏠 Domicile' }
        return labels[type] || type
    }

    const handleExportCSV = async () => {
        if (!exportDates.start || !exportDates.end) return toast.error("Veuillez sélectionner une période")
        try {
            toast.loading('Génération du fichier...', { id: 'exportCsv' })
            const response = await api.get('/api/invoices/export/csv', {
                params: { start_date: exportDates.start, end_date: exportDates.end },
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Compta_${exportDates.start}_au_${exportDates.end}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success('Export réussi !', { id: 'exportCsv' })
            setIsExportModalOpen(false)
        } catch (e) {
            toast.error("Erreur lors de l'export", { id: 'exportCsv' })
        }
    }

    const setQuickDate = (type) => {
        const today = new Date()
        if (type === 'current_month') {
            setExportDates({
                start: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
            })
        } else if (type === 'last_month') {
            setExportDates({
                start: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0]
            })
        } else if (type === 'year') {
            setExportDates({
                start: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0]
            })
        }
    }

    if (loading) return <div className="flex justify-center items-center h-64"><div className="spinner" /></div>

    const statCards = [
        { label: 'Patients',        value: stats?.total_patients || 0,          icon: Users,        color: 'bg-blue-100 text-blue-600',   link: '/admin/patients'     },
        { label: 'RDV cette semaine', value: stats?.appointments_this_week || 0, icon: Calendar,     color: 'bg-sage-100 text-sage-600',   link: '/admin/appointments' },
        { label: 'RDV à venir',     value: stats?.upcoming_appointments || 0,    icon: CalendarCheck, color: 'bg-sun-100 text-amber-600',  link: '/admin/appointments' },
        { label: 'Taux annulation', value: `${stats?.cancellation_rate || 0}%`,  icon: TrendingUp,   color: 'bg-peach-100 text-orange-600' },
    ]

    return (
        <div className="space-y-8">

            {/* HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-serif text-gray-800">Tableau de bord</h1>
                    <p className="text-gray-500 mt-1">Bienvenue, voici un aperçu de votre activité</p>
                </div>
                <button
                    onClick={() => { setQuickDate('current_month'); setIsExportModalOpen(true) }}
                    className="btn bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 shadow-sm transition-all"
                >
                    <FileSpreadsheet className="w-4 h-4 text-sage-600" /> Export Comptable
                </button>
            </div>

            {/* BANNIÈRE D'ALERTE SYSTEME NOTIFICATION */}
            {(sysConfig.sms_enabled === 'false' || sysConfig.smtp_enabled === 'false') && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center justify-center gap-4 text-sm animate-in fade-in">
                    <span className="flex items-center gap-1.5 text-orange-800 font-bold">
                        <AlertCircle className="w-4 h-4" /> Mode Silencieux Actif :
                    </span>
                    {sysConfig.sms_enabled === 'false' && <span className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded font-medium">SMS Auto OFF</span>}
                    {sysConfig.smtp_enabled === 'false' && <span className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded font-medium">Emails Auto OFF</span>}
                </div>
            )}

            {/* STAT CARDS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((stat) => (
                    <Link key={stat.label} to={stat.link || '#'} className="card hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-gray-500">{stat.label}</p>
                                <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                            </div>
                            <div className={`p-3 rounded-xl ${stat.color}`}><stat.icon className="w-5 h-5" /></div>
                        </div>
                    </Link>
                ))}
            </div>

            {/* ALERTES SMS */}
            {(stats?.pending_sms > 0 || stats?.failed_sms > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {stats?.pending_sms > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                            <Clock className="w-5 h-5 text-amber-600" />
                            <div>
                                <p className="font-medium text-amber-800">{stats.pending_sms} SMS en attente</p>
                                <p className="text-sm text-amber-600">Seront envoyés automatiquement</p>
                            </div>
                        </div>
                    )}
                    {stats?.failed_sms > 0 && (
                        <Link to="/admin/sms" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 hover:bg-red-100 transition-colors">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <div>
                                <p className="font-medium text-red-800">{stats.failed_sms} SMS en échec</p>
                                <p className="text-sm text-red-600">Cliquez pour voir les détails</p>
                            </div>
                        </Link>
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════
                ALERTES FORMULAIRES EN ATTENTE (> 48h)
            ═══════════════════════════════════════════════════════ */}
            {pendingFormAlerts.length > 0 && (
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-sm font-bold text-orange-700 uppercase tracking-wide flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Formulaires sans réponse
                        </h2>
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {pendingFormAlerts.length}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {pendingFormAlerts.map(alert => (
                            <div
                                key={alert.req_id}
                                className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-4 shadow-sm"
                            >
                                {/* Avatar */}
                                <div className="w-9 h-9 rounded-full bg-orange-200 text-orange-800 font-bold text-sm flex items-center justify-center flex-shrink-0">
                                    {alert.patient_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>

                                {/* Infos */}
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/admin/patients/${alert.patient_id}`}
                                        className="font-bold text-gray-800 text-sm hover:text-sage-600 hover:underline truncate block"
                                    >
                                        {alert.patient_name}
                                    </Link>
                                    <p className="text-xs text-gray-500 truncate">{alert.template_name}</p>
                                    <p className="text-[10px] text-orange-600 font-bold mt-0.5 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        En attente depuis {alert.days_waiting === 0 ? "aujourd'hui" : `${alert.days_waiting} jour${alert.days_waiting > 1 ? 's' : ''}`}
                                    </p>
                                </div>

                                {/* Bouton relancer */}
                                <button
                                    onClick={() => {
                                        setRelanceModal({
                                            req_id:        alert.req_id,
                                            template_name: alert.template_name,
                                            patient_name:  alert.patient_name,
                                            patient_email: alert.patient_email,
                                            patient_phone: alert.patient_phone,
                                            patient_id:    alert.patient_id
                                        })
                                        setSendVia({ email: !!alert.patient_email, sms: false })
                                    }}
                                    className="flex items-center gap-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg transition-colors flex-shrink-0"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    Relancer
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* SPLIT SCREEN : RDV & TÂCHES */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Prochains RDV */}
                <div className="card flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-serif text-gray-800">Vos 5 prochains rendez-vous</h2>
                        <Link to="/admin/appointments" className="text-sage-600 hover:text-sage-700 text-sm font-medium">Voir tout →</Link>
                    </div>
                    {upcomingAppointments.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-8 text-gray-500">
                            <CalendarCheck className="w-12 h-12 mb-3 opacity-50" />
                            <p>Aucun rendez-vous à venir</p>
                        </div>
                    ) : (
                        <div className="space-y-3">  
                            {upcomingAppointments.map((apt) => (
                                <div key={apt.id} className="relative group">  
                                    <Link
                                        to={`/admin/appointments?action=open&apt_id=${apt.id}`}
                                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-sage-50 transition-colors border border-transparent hover:border-sage-200 block"
                                    >
                                        <div className="w-14 text-center">
                                            <p className="text-xs text-gray-500 uppercase">{new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'short' })}</p>
                                            <p className="text-xl font-bold text-gray-800">{new Date(apt.date).getDate()}</p>
                                            <p className="text-xs uppercase font-bold">{new Date(apt.date).toLocaleDateString('fr-FR', { month: 'short' })}</p>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    navigate(`/admin/patients/${apt.patient_id}`);
                                                }}
                                                className="font-bold text-gray-800 hover:text-sage-600 hover:underline cursor-pointer truncate block text-sm sm:text-base"
                                            >
                                                {apt.patient_name}
                                            </span>
                                            <p className="text-sm text-gray-500">{apt.start_time?.slice(0, 5)} • {getConsultationTypeLabel(apt.consultation_type)}</p>
                                        </div>
                                    </Link>
                                    
                                    {/* Bouton en position absolue pour éviter le nesting dans le Link */}
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation(); // Stoppe la propagation au lieu de preventDefault
                                            navigate(`/admin/patients/${apt.patient_id}?action=new_note&apt_id=${apt.id}`);
                                        }}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-sage-600 text-white p-2 rounded-full shadow-sm hover:bg-sage-700 transition-transform hover:scale-105 opacity-0 group-hover:opacity-100 z-10"
                                        title="Démarrer la consultation"
                                    >
                                        <PlayCircle className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* TO-DO LIST */}
                <div className="card bg-yellow-50/30 border-yellow-200/50 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-serif text-gray-800 flex items-center gap-2">
                            📝 Pense-Bête
                        </h2>
                    </div>

                    {/* Ajout rapide */}
                    <form onSubmit={handleAddTask} className="flex gap-2 mb-4">
                        <input
                            type="text"
                            value={newTaskContent}
                            onChange={e => setNewTaskContent(e.target.value)}
                            placeholder="Ex: Appeler Marie..."
                            className="flex-1 text-sm bg-white border-yellow-200 focus:border-yellow-400 focus:ring-yellow-100 px-3 py-2 rounded-lg"
                        />
                        <input
                            type="date"
                            value={newTaskDate}
                            onChange={e => setNewTaskDate(e.target.value)}
                            className="w-36 text-sm bg-white border-yellow-200 focus:border-yellow-400 px-2 py-2 rounded-lg text-gray-600"
                            title="Date d'échéance (optionnel)"
                        />
                        <button
                            type="submit"
                            disabled={!newTaskContent.trim()}
                            className="btn bg-yellow-400 hover:bg-yellow-500 text-yellow-900 px-4 py-2 rounded-lg transition-colors"
                        >
                            <Plus className="w-5 h-5"/>
                        </button>
                    </form>

                    <div className="flex-1 overflow-y-auto space-y-2">
                        {tasks.length === 0 ? (
                            <div className="text-center text-gray-400 italic py-8">Tout est à jour ! 🎉</div>
                        ) : (
                            tasks.map(task => {
                                const isWaitlist = task.content.includes("🚨 LISTE D'ATTENTE");
                                
                                return (
                                <div key={task.id} className={`flex items-start gap-3 p-3 rounded-lg shadow-sm group border ${isWaitlist ? 'bg-red-50 border-red-200' : 'bg-white border-yellow-100'}`}>
                                    <button
                                        onClick={() => toggleTask(task)}
                                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center text-transparent transition-colors shrink-0 ${isWaitlist ? 'border-red-400 hover:bg-red-200 hover:text-red-700' : 'border-yellow-300 hover:bg-yellow-100 hover:text-yellow-600'}`}
                                    >
                                        <Check className="w-3 h-3" />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        {isWaitlist ? (
                                            <>
                                                <p className="text-[10px] font-bold text-red-600 uppercase mb-0.5">Demande Patient</p>
                                                <p className="text-sm font-bold text-red-900 whitespace-pre-line leading-relaxed">{task.content.replace("🚨 LISTE D'ATTENTE : ", "")}</p>
                                            </>
                                        ) : (
                                            <p className="text-sm text-gray-800">{task.content}</p>
                                        )}
                                        
                                        {task.patient_name && !isWaitlist && (
                                            <Link to={`/admin/patients/${task.patient_id}`} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full hover:underline mt-1 inline-block">
                                                👤 {task.patient_name}
                                            </Link>
                                        )}
                                        {task.due_date && (
                                            <span className={`text-[10px] font-medium ml-2 px-1.5 py-0.5 rounded ${isWaitlist ? 'text-red-700 bg-red-100' : 'text-red-500 bg-red-50'}`}>
                                                ⏳ {new Date(task.due_date).toLocaleDateString('fr-FR')}
                                            </span>
                                        )}
                                    </div>
                                    {/* Bouton Relancer visible uniquement sur les tâches auto de suivi */}
                                    {task.patient_id && task.content.includes('Proposer un suivi') && (
                                        <button
                                            onClick={() => navigate(`/admin/patients/${task.patient_id}?action=send_form`)}
                                            className="text-[10px] bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors"
                                        >
                                            <Send className="w-2.5 h-2.5" /> Relancer
                                        </button>
                                    )}
                                </div>
                            )})
                        )}
                    </div>
                </div>
            </div>

            {/* MODALE EXPORT CSV */}
            {isExportModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 animate-in zoom-in-95">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-serif font-bold text-gray-800 flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-sage-600" /> Export Comptable
                            </h3>
                            <button onClick={() => setIsExportModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                <button onClick={() => setQuickDate('last_month')}    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Mois précédent</button>
                                <button onClick={() => setQuickDate('current_month')} className="text-xs bg-sage-100 hover:bg-sage-200 text-sage-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Ce mois-ci</button>
                                <button onClick={() => setQuickDate('year')}          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Cette année</button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Du</label>
                                    <input type="date" value={exportDates.start} onChange={e => setExportDates({...exportDates, start: e.target.value})} className="w-full text-sm py-2" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Au</label>
                                    <input type="date" value={exportDates.end} onChange={e => setExportDates({...exportDates, end: e.target.value})} className="w-full text-sm py-2" />
                                </div>
                            </div>
                            <button onClick={handleExportCSV} className="w-full btn btn-primary flex justify-center items-center gap-2 mt-4">
                                <Download className="w-4 h-4" /> Télécharger le CSV
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODALE RELANCE FORMULAIRE (depuis le dashboard) */}
            {relanceModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg font-serif font-bold text-gray-800 flex items-center gap-2">
                                <Send className="w-5 h-5 text-orange-500" /> Relancer le patient
                            </h3>
                            <button onClick={() => setRelanceModal(null)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
                        </div>

                        {/* Rappel contexte */}
                        <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 mb-5 space-y-1">
                            <p className="text-xs text-orange-600 font-bold uppercase">Formulaire en attente</p>
                            <p className="text-sm font-bold text-gray-800">{relanceModal.template_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <Link
                                    to={`/admin/patients/${relanceModal.patient_id}`}
                                    onClick={() => setRelanceModal(null)}
                                    className="text-xs text-sage-600 hover:underline font-medium flex items-center gap-1"
                                >
                                    👤 {relanceModal.patient_name}
                                    <ChevronRight className="w-3 h-3" />
                                </Link>
                            </div>
                        </div>

                        {/* Canal */}
                        <div className="mb-5">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-3">Envoyer via</label>
                            <div className="grid grid-cols-2 gap-3">

                                {/* Email */}
                                <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${
                                    sendVia.email ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'
                                } ${!relanceModal.patient_email ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                    <input type="checkbox" className="hidden"
                                        checked={sendVia.email} disabled={!relanceModal.patient_email}
                                        onChange={e => setSendVia(p => ({ ...p, email: e.target.checked }))}
                                    />
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                        sendVia.email ? 'bg-orange-500' : 'bg-white border-2 border-gray-300'
                                    }`}>
                                        {sendVia.email && <CheckCircle className="w-4 h-4 text-white" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[90px]">{relanceModal.patient_email || 'Non renseigné'}</p>
                                    </div>
                                </label>

                                {/* SMS */}
                                <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${
                                    sendVia.sms ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                                } ${!relanceModal.patient_phone ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                    <input type="checkbox" className="hidden"
                                        checked={sendVia.sms} disabled={!relanceModal.patient_phone}
                                        onChange={e => setSendVia(p => ({ ...p, sms: e.target.checked }))}
                                    />
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                        sendVia.sms ? 'bg-blue-500' : 'bg-white border-2 border-gray-300'
                                    }`}>
                                        {sendVia.sms && <CheckCircle className="w-4 h-4 text-white" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> SMS</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">{relanceModal.patient_phone || 'Non renseigné'}</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <button
                            onClick={handleRelance}
                            disabled={isSending || (!sendVia.email && !sendVia.sms)}
                            className="w-full btn bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSending
                                ? <><div className="spinner w-4 h-4 border-white" /> Envoi...</>
                                : <><Send className="w-4 h-4" /> Envoyer la relance</>
                            }
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}